import { Icon } from "@iconify/react";
import { CatalogPlan, ProxyTopupPackage } from "@/services/stripe";
import { formatCount, formatMoney, perThousand } from "@/utils/billingFormat";
import { planRank } from "./types";
import Button, { ButtonSpinner } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/AsyncState";

// Credit packages. The backend maps each amount to its Stripe price.
export const CREDIT_TOPUPS = [
  { credits: 500, priceCents: 500 },
  { credits: 1200, priceCents: 1000 },
  { credits: 5000, priceCents: 3500 },
];

interface Props {
  currentPlan: string;
  catalog: CatalogPlan[];
  proxyPackages: ProxyTopupPackage[];
  /** "loading" while the packages are requested, "error" when that failed (with onRetryProxy). */
  proxyState?: "loading" | "error" | "ready";
  onRetryProxy?: () => void;
  buying: string | null;
  onBuyCredits: (credits: number) => void;
  onBuyProxy: (bandwidthMb: number) => void;
}

/** A price tile, as on spidra.io/pricing: the price large, what it buys under it. */
function Tile({
  title,
  sub,
  price,
  busy,
  disabled,
  onBuy,
}: {
  title: string;
  sub?: string;
  price: string;
  busy: boolean;
  disabled: boolean;
  onBuy: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onBuy}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      aria-label={`Buy ${title} for ${price}`}
      className="group relative rounded-card border border-line bg-surface px-4 py-3.5 text-left transition-all enabled:cursor-pointer enabled:hover:-translate-y-0.5 enabled:hover:border-primary/40 enabled:hover:shadow-popover disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary motion-reduce:transition-none motion-reduce:hover:translate-y-0"
    >
      <span className="flex items-center justify-between gap-2">
        <span className="text-xl font-medium tracking-[-0.015em] text-fg tabular-nums group-enabled:group-hover:text-primary-text">
          {price}
        </span>
        {busy ? (
          <ButtonSpinner />
        ) : (
          <Icon
            icon="mdi:arrow-right"
            className="text-base text-fg-muted opacity-0 transition-opacity group-enabled:group-hover:opacity-100"
            aria-hidden="true"
          />
        )}
      </span>
      <span className="mt-0.5 block text-13 text-fg">{title}</span>
      {sub && <span className="block text-xs text-fg-muted">{sub}</span>}
    </button>
  );
}

function Panel({
  icon,
  title,
  line,
  children,
}: {
  icon: string;
  title: string;
  line: string;
  children: React.ReactNode;
}) {
  return (
    <div className="p-5 sm:p-6">
      <div className="flex items-center gap-3">
        <span className="bg-deep flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white shadow-card">
          <Icon icon={icon} className="text-lg" aria-hidden="true" />
        </span>
        <div>
          <h3 className="text-15 font-medium text-fg">{title}</h3>
          <p className="text-13 text-fg-muted">{line}</p>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-1 gap-2.5 min-[420px]:grid-cols-3">{children}</div>
    </div>
  );
}

export default function TopUps({
  currentPlan,
  catalog,
  proxyPackages,
  proxyState = "ready",
  onRetryProxy,
  buying,
  onBuyCredits,
  onBuyProxy,
}: Props) {
  const isFree = currentPlan === "Free";
  const locked = isFree || !!buying;
  const nextPlan = catalog.find(
    p => planRank(p.name) === planRank(currentPlan) + 1 && p.name !== "Free"
  );
  // Only point at the next plan when it really is cheaper per credit than every top-up.
  const cheapestTopUp = Math.min(...CREDIT_TOPUPS.map(t => t.priceCents / t.credits));
  const showUpgradeHint =
    !isFree &&
    !!nextPlan &&
    !!nextPlan.includedCredits &&
    nextPlan.priceCents / nextPlan.includedCredits < cheapestTopUp;

  return (
    <div className="space-y-3">
      {isFree && (
        <p className="flex items-start gap-2 text-sm text-fg-secondary rounded-lg bg-surface-muted p-3">
          <Icon icon="mdi:information-outline" className="text-lg text-fg-muted shrink-0 mt-px" />
          Top-ups are for paid plans. Choose a plan above first, then you can add credits or
          bandwidth any time.
        </p>
      )}
      <div className="grid grid-cols-1 overflow-hidden rounded-card border border-line bg-surface shadow-card lg:grid-cols-2">
        <div className="border-b border-line lg:border-b-0 lg:border-r">
          <Panel icon="mdi:lightning-bolt" title="Credits" line="Used after your monthly credits">
            {CREDIT_TOPUPS.map(pkg => (
              <Tile
                key={pkg.credits}
                title={`${formatCount(pkg.credits)} credits`}
                sub={`${perThousand(pkg.priceCents, pkg.credits)} per 1,000`}
                price={formatMoney(pkg.priceCents)}
                busy={buying === `credits-${pkg.credits}`}
                disabled={locked}
                onBuy={() => onBuyCredits(pkg.credits)}
              />
            ))}
          </Panel>
        </div>
        <div className="bg-surface-muted">
          <Panel
            icon="mdi:earth"
            title="Proxy bandwidth"
            line="For stealth mode, used after your plan's bandwidth"
          >
            {proxyPackages.map(pkg => (
              <Tile
                key={pkg.bandwidthMb}
                title={pkg.label}
                price={formatMoney(pkg.priceCents)}
                busy={buying === `proxy-${pkg.bandwidthMb}`}
                disabled={locked}
                onBuy={() => onBuyProxy(pkg.bandwidthMb)}
              />
            ))}
            {proxyState === "loading" &&
              proxyPackages.length === 0 &&
              [0, 1, 2].map(i => <Skeleton key={i} className="h-[74px] rounded-card" />)}
            {proxyState !== "loading" && proxyPackages.length === 0 && (
              <div
                className="col-span-full flex flex-wrap items-center justify-between gap-2 py-2 text-sm text-fg-muted"
                role="status"
              >
                Bandwidth packages couldn&apos;t be loaded.
                {onRetryProxy && (
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon="mdi:refresh"
                    onClick={onRetryProxy}
                  >
                    Retry
                  </Button>
                )}
              </div>
            )}
          </Panel>
        </div>
      </div>
      {showUpgradeHint && (
        <p className="text-xs text-fg-muted">
          Need extra credits every month? {nextPlan!.name} costs{" "}
          {perThousand(nextPlan!.priceCents, nextPlan!.includedCredits!)} per 1,000 credits, less
          than any top-up.
        </p>
      )}
    </div>
  );
}
